/*
  The underwriting details, in ONE form.

  The app asks these one question at a time, which is right for a customer alone with a phone and
  wrong for an agent with the logbook photo open in front of them. So: everything on screen at
  once, saved in one submission.

  What the app and this share is the VALIDATION. Every field goes through the same validators on
  the way in, because two doors accepting different data into one table is how the table stops
  meaning anything.

  **One bad field does not cost the other seven.** The endpoint saves whatever validated and names
  what did not, and this maps those names back onto the form. An agent retyping a whole form
  because one date was wrong is how a tool stops being used.
*/
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Button, Card, Col, DatePicker, Descriptions, Form, Input, InputNumber, Row, Space, Tag } from 'antd'
import moment from 'moment'
import {
  patchRequest,
  insurancePolicyDetailsUrl,
  errorNotification,
  successNotification
} from '../../config'

// The server names its fields in snake_case, which is what the intake engine records. The form
// names them in camelCase, like every other payload here. Mapped rather than guessed: a mismatch
// would silently show no error at all, which is the worst of the three possible outcomes.
const FIELD_NAMES = {
  registration_number: 'registrationNumber',
  chassis_number: 'chassisNumber',
  make: 'make',
  model: 'model',
  year_of_manufacture: 'yearOfManufacture',
  owner_name: 'ownerName',
  start_date: 'startDate',
  email: 'email'
}

const AUTO_SLOTS = {
  registrationNumber: 'registration_number',
  chassisNumber: 'chassis_number',
  make: 'make',
  model: 'model',
  yearOfManufacture: 'year_of_manufacture'
}

const wasDerived = (policy, name) =>
  Array.isArray(policy.autoFilled) && policy.autoFilled.includes(AUTO_SLOTS[name])

/* A field label that says when nobody typed the value in it. */
const Label = ({ text, policy, name }) => (
  <span>
    {text}
    {wasDerived(policy, name) ? (
      <Tag style={{ marginLeft: 6 }} color="blue">
        auto
      </Tag>
    ) : null}
  </span>
)

Label.propTypes = {
  text: PropTypes.string,
  policy: PropTypes.object.isRequired,
  name: PropTypes.string
}

const PolicyDetailsForm = ({ policy, onChanged }) => {
  const [form] = Form.useForm()
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState(false)

  const complete = Boolean(
    policy.registrationNumber &&
      policy.make &&
      policy.model &&
      policy.yearOfManufacture &&
      policy.ownerName &&
      policy.startDate
  )

  // Whatever the server holds, in the form. Extracted because it is needed twice: when a
  // different policy opens, and again after a save — the server may have filled fields the agent
  // left blank.
  const fill = target =>
    form.setFieldsValue({
      registrationNumber: target.registrationNumber || '',
      chassisNumber: target.chassisNumber || '',
      make: target.make || '',
      model: target.model || '',
      yearOfManufacture: target.yearOfManufacture || undefined,
      ownerName: target.ownerName || '',
      startDate: target.startDate ? moment(target.startDate) : undefined,
      email: target.email || ''
    })

  useEffect(() => {
    fill(policy)
    // eslint-disable-next-line
  }, [policy.id])

  const save = async values => {
    setBusy(true)
    const response = await patchRequest(
      insurancePolicyDetailsUrl(policy.id),
      {
        registrationNumber: values.registrationNumber || '',
        chassisNumber: values.chassisNumber || '',
        make: values.make || '',
        model: values.model || '',
        yearOfManufacture: values.yearOfManufacture || '',
        ownerName: values.ownerName || '',
        startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : '',
        email: values.email || ''
      },
      'details',
      () => {}
    )
    setBusy(false)
    if (!response.success) {
      errorNotification(
        response.error?.response?.data?.detail || 'Those details could not be saved.'
      )
      return
    }

    const errors = response.data.errors || {}
    const bad = Object.keys(errors)
    if (bad.length) {
      form.setFields(
        bad.map(field => ({ name: FIELD_NAMES[field] || field, errors: [errors[field]] }))
      )
      // Said out loud, because the form now looks half-red and the agent needs to know the rest
      // of it is safely in rather than assuming nothing saved.
      errorNotification(
        `Saved everything except ${bad.length === 1 ? 'one field' : `${bad.length} fields`}. ` +
          'The rest is in — fix the marked ones and save again.'
      )
    } else {
      successNotification('Details saved.')
      setEditing(false)
    }

    /*
      Refilled from what the SERVER now holds, which is not always what was typed.

      A chassis number is decoded on the way in, so an agent who typed the VIN and left make,
      model and year blank gets them back filled — and without this they would sit there empty,
      looking unsaved, until the drawer was closed and reopened. The typed fields are unaffected:
      the server echoes back exactly what it stored.
    */
    fill(response.data.policy)
    onChanged(response.data.policy)
  }

  // Once it is complete it collapses to a summary. A finished form taking up half the drawer is
  // half the drawer spent on something nobody is going to change.
  if (complete && !editing) {
    return (
      <Card
        size="small"
        title="The car"
        extra={
          <Button size="small" onClick={() => setEditing(true)}>
            Edit
          </Button>
        }
      >
        <Descriptions size="small" column={{ xs: 1, md: 2 }}>
          <Descriptions.Item label={<Label text="Registration" policy={policy} name="registrationNumber" />}>
            {policy.registrationNumber || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={<Label text="Chassis" policy={policy} name="chassisNumber" />}>
            {policy.chassisNumber || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={<Label text="Make / model" policy={policy} name="make" />}>
            {[policy.make, policy.model].filter(Boolean).join(' ') || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={<Label text="Year" policy={policy} name="yearOfManufacture" />}>
            {policy.yearOfManufacture || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Owner">{policy.ownerName || '—'}</Descriptions.Item>
          <Descriptions.Item label="Cover starts">{policy.startDate || '—'}</Descriptions.Item>
          <Descriptions.Item label="Email">{policy.email || '—'}</Descriptions.Item>
        </Descriptions>
        {policy.carDescription ? (
          <div style={{ marginTop: 8 }}>
            <div style={{ opacity: 0.6, fontSize: 12 }}>In the customer&apos;s own words</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{policy.carDescription}</div>
          </div>
        ) : null}
      </Card>
    )
  }

  return (
    <Card
      size="small"
      title="The car"
      extra={
        complete ? (
          <Button size="small" onClick={() => setEditing(false)}>
            Done
          </Button>
        ) : null
      }
    >
      {!policy.insurerName ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="No insurer chosen yet."
          description="These can be filled in now, but the policy cannot be paid for until an insurer is picked and the price is fixed."
        />
      ) : null}

      <Form form={form} layout="vertical" onFinish={save}>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item
              name="registrationNumber"
              label={<Label text="Registration number" policy={policy} name="registrationNumber" />}
            >
              <Input placeholder="GT 1234-24" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="chassisNumber"
              label={<Label text="Chassis number (VIN)" policy={policy} name="chassisNumber" />}
              extra="17 characters. We decode it and fill in what it tells us."
            >
              <Input placeholder="JH4KA7561PC008269" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="make" label={<Label text="Make" policy={policy} name="make" />}>
              <Input placeholder="Toyota" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="model" label={<Label text="Model" policy={policy} name="model" />}>
              <Input placeholder="Corolla" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="yearOfManufacture"
              label={<Label text="Year" policy={policy} name="yearOfManufacture" />}
            >
              <InputNumber style={{ width: '100%' }} placeholder="2018" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            {/* Never auto-filled, by design: a VIN does not know who owns the car. */}
            <Form.Item name="ownerName" label="Owner's full name">
              <Input placeholder="As it appears on their ID" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="startDate" label="Cover starts">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="email" label="Email (optional)">
              <Input placeholder="name@example.com" />
            </Form.Item>
          </Col>
        </Row>
        <Space>
          <Button type="primary" loading={busy} onClick={() => form.submit()}>
            Save details
          </Button>
          <span style={{ opacity: 0.65, fontSize: 12 }}>
            Whatever is valid is saved, even if something else is not.
          </span>
        </Space>
      </Form>
    </Card>
  )
}

PolicyDetailsForm.propTypes = {
  policy: PropTypes.object.isRequired,
  onChanged: PropTypes.func.isRequired
}

export default PolicyDetailsForm
